import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaPlus, FaEdit, FaTrash, FaUser, FaFileExcel } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getClients, searchClients, deleteClient, createClient, updateClient } from '../services/clientService';
import toast from 'react-hot-toast';
import CustomModal from '../components/Modal';
import ClientForm from '../components/forms/ClientForm';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { exportClientsToExcel } from '../utils/excelExport';
import Pagination from '../components/Pagination';


function Clients() {
  const { isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  
  // State برای فیلترها و modal
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const formRef = useRef(null);
  
  // React Query hooks
  const queryClient = useQueryClient();
  
  // دریافت لیست مراجعان
  const {
    data: clientsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['clients', currentPage, itemsPerPage, searchTerm],
    () => searchTerm ? searchClients(searchTerm) : getClients({ page: currentPage, limit: itemsPerPage }),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه - کاهش زمان cache برای آمار به‌روز
    }
  );

  // Mutation برای حذف مراجع
  const deleteClientMutation = useMutation(deleteClient, {
    onSuccess: () => {
      toast.success('مراجع با موفقیت حذف شد');
      queryClient.invalidateQueries(['clients']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در حذف مراجع');
    }
  });

  // Mutation برای ایجاد مراجع
  const createClientMutation = useMutation(createClient, {
    onSuccess: () => {
      toast.success('مراجع با موفقیت اضافه شد');
      queryClient.invalidateQueries(['clients']);
      setIsModalOpen(false);
      setEditingClient(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در افزودن مراجع');
    }
  });

  // Mutation برای بروزرسانی مراجع
  const updateClientMutation = useMutation(
    ({ clientId, clientData }) => updateClient(clientId, clientData),
    {
      onSuccess: () => {
        toast.success('مراجع با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['clients']);
        setIsModalOpen(false);
        setEditingClient(null);
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در بروزرسانی مراجع');
      }
    }
  );

  // تابع حذف مراجع
  const handleDeleteClient = (clientId) => {
    if (window.confirm('آیا از حذف این مراجع اطمینان دارید؟')) {
      deleteClientMutation.mutate(clientId);
    }
  };

  const handleViewProfile = (client) => {
    navigate(`/client-profile/${client._id || client.id}`);
  };

  const handleExportToExcel = () => {
    try {
      exportClientsToExcel(clients);
      toast.success('فایل اکسل با موفقیت دانلود شد');
    } catch (error) {
      console.error('خطا در export اکسل:', error);
      toast.error('خطا در دانلود فایل اکسل');
    }
  };

  // تابع باز کردن modal برای افزودن
  const handleAddClient = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };

  // تابع باز کردن modal برای ویرایش
  const handleEditClient = async (client) => {
    setEditingClient(client);
    setIsModalOpen(true);
  };



  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

    // تابع ارسال فرم
  const handleSubmitForm = (data) => {
    // پاک کردن داده‌های فرم برای جلوگیری از circular reference
    const cleanData = {
      firstName: data.firstName,
      lastName: data.lastName,
      nationalId: data.nationalId,
      phone: data.phone,
      email: data.email || '',
      address: data.address || '',
      occupation: data.occupation || '',
      birthDate: data.birthDate,
      consultant: data.consultant,
      notes: data.notes || '',
      emergencyContact: data.emergencyContact || '',
      emergencyPhone: data.emergencyPhone || ''
    };
    
    // بررسی وجود birthDate
    if (!cleanData.birthDate || cleanData.birthDate.trim() === '') {
      toast.error('تاریخ تولد الزامی است');
      return;
    }
    
    if (editingClient) {
      // ویرایش مراجع موجود
      updateClientMutation.mutate({ clientId: editingClient._id || editingClient.id, clientData: cleanData });
    } else {
      // افزودن مراجع جدید
      createClientMutation.mutate(cleanData);
    }
  };

  // تابع جستجو
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // بازگشت به صفحه اول
  };

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری مراجعان...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  // داده‌های مراجعان
  const clients = clientsData?.data?.clients || clientsData?.clients || clientsData?.data || clientsData || [];

  return (
    <>
      <Flex direction="column"  dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Box p="20px 0px 35px 0px">
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  مدیریت مراجعان
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهده و مدیریت اطلاعات مراجعان کلینیک
                </Text>
              </Flex>
            </Box>
            <Box px="0px" pt="0px" pb="25px">
              <Flex px="25px" mb="25px" justifyContent="space-between" alignItems="center">
                <HStack spacing="15px">
                  <InputGroup maxW="400px">
                    <InputLeftElement>
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="جستجو در مراجعان..."
                      borderRadius="15px"
                      fontSize="sm"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </InputGroup>
                </HStack>
                <HStack spacing={3}>
                  {(isAdmin() || isManager()) && (
                    <Button
                      leftIcon={<FaFileExcel />}
                      colorScheme="green"
                      borderRadius="15px"
                      fontSize="sm"
                      fontWeight="bold"
                      px="20px"
                      py="10px"
                      onClick={handleExportToExcel}
                    >
                      خروجی اکسل
                    </Button>
                  )}
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="teal"
                    borderRadius="15px"
                    fontSize="sm"
                    fontWeight="bold"
                    px="20px"
                    py="10px"
                    onClick={handleAddClient}
                  >
                    افزودن مراجع جدید
                  </Button>
                </HStack>
              </Flex>
              
              {/* جدول مراجعان */}
              <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                <Table variant="simple" color={textColor} size="sm">
                  <Thead>
                    <Tr my=".8rem" pl="0px" color="gray.400">
                      <Th borderColor={borderColor} textAlign="right">
                        نام و نام خانوادگی
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        کد ملی
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        شماره پرونده
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        مشاور
                      </Th>

                      <Th borderColor={borderColor} textAlign="right">
                        شغل
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        تاریخ تولد
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        وضعیت
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        آخرین مراجعه
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        عملیات
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {clients.length === 0 ? (
                      <Tr>
                        <Td colSpan={9} textAlign="center" py={8}>
                          <Text color="gray.500">هیچ مراجعه‌ای یافت نشد</Text>
                        </Td>
                      </Tr>
                    ) : (
                      clients.map((client) => (
                        <Tr key={client._id || client.id}>
                          <Td minWidth={{ sm: "250px" }} pl="0px">
                            <Flex direction="column">
                              <Text
                                fontSize="md"
                                color={textColor}
                                fontWeight="bold"
                                minWidth="100%"
                              >
                                {client.firstName && client.lastName 
                                  ? `${client.firstName} ${client.lastName}`
                                  : client.name || 'نامشخص'
                                }
                              </Text>
                              <Text fontSize="sm" color="gray.400" fontWeight="normal">
                                {client.phone || 'شماره تماس ثبت نشده'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {client.nationalId || 'کد ملی ثبت نشده'}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {client.fileNumber || 'شماره پرونده ثبت نشده'}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {client.consultant?.name || 'مشاور تعیین نشده'}
                            </Text>
                          </Td>

                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {client.occupation || 'شغل ثبت نشده'}
                            </Text>
                          </Td>
                          <Td>
                            <JalaliDateDisplay
                              date={client.birthDate}
                              format="jYYYY/jMM/jDD"
                              textProps={{
                                fontSize: "md",
                                color: textColor,
                                fontWeight: "bold"
                              }}
                            />
                          </Td>
                          <Td>
                            <Badge
                              bg={client.active !== false ? "green.400" : "gray.400"}
                              color="white"
                              fontSize="16px"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {client.active !== false ? "فعال" : "غیرفعال"}
                            </Badge>
                          </Td>
                          <Td>
                            {client.lastVisit ? (
                              <JalaliDateDisplay
                                date={client.lastVisit.split(' ')[0]}
                                format="jYYYY/jMM/jDD"
                                textProps={{
                                  fontSize: "md",
                                  color: textColor,
                                  fontWeight: "bold"
                                }}
                              />
                            ) : (
                              <Text fontSize="md" color="gray.400" fontWeight="bold" pb=".5rem">
                                بدون مراجعه
                              </Text>
                            )}
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              {isManager() && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                  leftIcon={<FaUser />}
                                  onClick={() => handleViewProfile(client)}
                                >
                                  مشاهده پروفایل
                                </Button>
                              )}
                              <Button
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                leftIcon={<FaEdit />}
                                onClick={() => handleEditClient(client)}
                              >
                                ویرایش
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FaTrash />}
                                onClick={() => handleDeleteClient(client._id || client.id)}
                                isLoading={deleteClientMutation.isLoading}
                              >
                                حذف
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {clientsData?.data?.pagination && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={clientsData.data.pagination.pages}
                  totalItems={clientsData.data.pagination.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal فرم مراجع */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingClient ? 'ویرایش مراجع' : 'افزودن مراجع جدید'}
        onSubmit={() => {
          formRef.current?.submit();
        }}
        isLoading={createClientMutation.isLoading || updateClientMutation.isLoading}
        submitText={editingClient ? 'بروزرسانی' : 'افزودن'}
        size="xl"
      >
        <ClientForm
          ref={formRef}
          client={editingClient}
          onSubmit={handleSubmitForm}
          isLoading={createClientMutation.isLoading || updateClientMutation.isLoading}
        />
      </CustomModal>
    </>
  );
}

export default Clients; 